import React, { useState } from "react";
import { db } from "../lib/instant";
import { useAuth } from "../context/AuthContext";
import { id as instantId } from "../lib/instant";
import { Star, MessageSquare, ThumbsUp } from "lucide-react";
import toast from "react-hot-toast";

export default function Reviews({ productId, theme }) {
  const primaryColor = theme?.primaryColor || "#6b2118";
  const accentColor  = theme?.accentColor  || "#c9a84c";
  const secondaryColor = theme?.secondaryColor || "#2e7d32";
  const { user } = useAuth();

  const [rating, setRating]     = useState(0);
  const [hovered, setHovered]   = useState(0);
  const [comment, setComment]   = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const { data } = db.useQuery({
    reviews: { $: { where: { productId } } },
  });
  const reviews = [...(data?.reviews || [])].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );

  const avgRating = reviews.length
    ? reviews.reduce((s, r) => s + Number(r.rating), 0) / reviews.length
    : 0;

  const alreadyReviewed = user && reviews.some(r => r.userId === user.id);

  const handleSubmit = async () => {
    if (!user)   { toast.error("Please login to leave a review"); return; }
    if (!rating) { toast.error("Please select a star rating");    return; }
    setSubmitting(true);
    try {
      await db.transact([
        db.tx.reviews[instantId()].update({
          productId,
          userId:    user.id,
          userName:  user.email?.split("@")[0] || "Customer",
          rating,
          comment:   comment.trim(),
          createdAt: new Date().toISOString(),
        }),
      ]);
      toast.success("Review submitted! Thank you 🌟");
      setRating(0); setComment(""); setShowForm(false);
    } catch { toast.error("Failed to submit review"); }
    setSubmitting(false);
  };

  const StarRow = ({ value, interactive = false, size = 18 }) => (
    <div style={{ display: "flex", gap: 3 }}>
      {[1,2,3,4,5].map(s => (
        <Star
          key={s}
          size={size}
          fill={s <= (interactive ? (hovered || value) : value) ? accentColor : "none"}
          color={accentColor}
          style={{ cursor: interactive ? "pointer" : "default", transition: "transform 0.1s" }}
          onMouseEnter={() => interactive && setHovered(s)}
          onMouseLeave={() => interactive && setHovered(0)}
          onClick={() => interactive && setRating(s)}
        />
      ))}
    </div>
  );

  const ratingBars = [5,4,3,2,1].map(n => ({
    n,
    count: reviews.filter(r => Math.round(r.rating) === n).length,
  }));

  return (
    <div style={{ marginTop: 64 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 32, flexWrap: "wrap", gap: 16 }}>
        <div>
          <div style={{ fontSize: "0.72rem", letterSpacing: 4, textTransform: "uppercase", color: secondaryColor, fontWeight: 700, marginBottom: 6 }}>Customer Reviews</div>
          <h2 style={{ fontFamily: "Georgia, serif", fontSize: "1.8rem", color: "#2c1810" }}>
            What People <span style={{ color: secondaryColor }}>Say</span>
          </h2>
        </div>
        {user && !alreadyReviewed && (
          <button
            onClick={() => setShowForm(f => !f)}
            style={{ background: primaryColor, color: "#fff", padding: "10px 22px", border: "none", borderRadius: 10, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, fontSize: "0.88rem" }}
          >
            <MessageSquare size={15}/> Write a Review
          </button>
        )}
        {!user && (
          <p style={{ fontSize: "0.82rem", color: "#aaa" }}>
            <a href="/login" style={{ color: primaryColor, fontWeight: 700 }}>Login</a> to leave a review
          </p>
        )}
      </div>

      {/* Summary bar */}
      {reviews.length > 0 && (
        <div style={{ background: "#fff", borderRadius: 16, padding: 28, marginBottom: 28, display: "flex", gap: 40, alignItems: "center", flexWrap: "wrap", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontFamily: "Georgia, serif", fontSize: "3.5rem", fontWeight: 800, color: primaryColor, lineHeight: 1 }}>{avgRating.toFixed(1)}</div>
            <StarRow value={Math.round(avgRating)} size={16} />
            <div style={{ fontSize: "0.75rem", color: "#aaa", marginTop: 4 }}>{reviews.length} review{reviews.length !== 1 ? "s" : ""}</div>
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            {ratingBars.map(({ n, count }) => (
              <div key={n} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <span style={{ fontSize: "0.78rem", color: "#888", width: 8 }}>{n}</span>
                <Star size={12} fill={accentColor} color={accentColor} />
                <div style={{ flex: 1, height: 8, background: "#f0f0f0", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ width: reviews.length ? `${(count / reviews.length) * 100}%` : "0%", height: "100%", background: accentColor, borderRadius: 4, transition: "width 0.5s" }} />
                </div>
                <span style={{ fontSize: "0.75rem", color: "#aaa", width: 20 }}>{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Write review form */}
      {showForm && (
        <div style={{ background: "#fff", borderRadius: 16, padding: 28, marginBottom: 28, border: `2px solid ${primaryColor}20`, boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
          <h3 style={{ fontFamily: "Georgia, serif", fontSize: "1.2rem", color: "#2c1810", marginBottom: 20 }}>Your Review</h3>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#555", marginBottom: 10 }}>Rating *</div>
            <StarRow value={rating} interactive size={28} />
            <div style={{ fontSize: "0.75rem", color: "#aaa", marginTop: 6 }}>
              {["","Poor","Fair","Good","Very Good","Excellent"][hovered || rating] || "Tap to rate"}
            </div>
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 700, color: "#555", marginBottom: 6 }}>Your Review (Optional)</label>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              rows={4}
              placeholder="Share your experience with ACHU Health Mix..."
              style={{ width: "100%", padding: "12px 14px", border: "1.5px solid #e0e0e0", borderRadius: 10, fontSize: "0.9rem", outline: "none", resize: "vertical", fontFamily: "inherit" }}
            />
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <button onClick={() => setShowForm(false)} style={{ flex: 1, padding: 12, border: "1.5px solid #e0e0e0", background: "none", borderRadius: 10, cursor: "pointer", fontWeight: 600 }}>Cancel</button>
            <button onClick={handleSubmit} disabled={submitting || !rating} style={{ flex: 2, padding: 12, background: submitting || !rating ? "#ccc" : primaryColor, color: "#fff", border: "none", borderRadius: 10, cursor: submitting || !rating ? "not-allowed" : "pointer", fontWeight: 700 }}>
              {submitting ? "Submitting..." : "Submit Review"}
            </button>
          </div>
        </div>
      )}

      {/* Reviews list */}
      {reviews.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 0", background: "#fff", borderRadius: 16, color: "#bbb" }}>
          <MessageSquare size={40} style={{ marginBottom: 12 }} />
          <p>No reviews yet. Be the first to review!</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {reviews.map(review => (
            <div key={review.id} style={{ background: "#fff", borderRadius: 14, padding: "22px 24px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)", borderLeft: `4px solid ${accentColor}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: "50%", background: `${primaryColor}20`, color: primaryColor, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "1rem" }}>
                    {(review.userName || "C")[0].toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, color: "#2c1810", fontSize: "0.92rem" }}>{review.userName || "Customer"}</div>
                    <StarRow value={review.rating} size={13} />
                  </div>
                </div>
                <div style={{ fontSize: "0.75rem", color: "#bbb" }}>
                  {review.createdAt ? new Date(review.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}
                </div>
              </div>
              {review.comment && (
                <p style={{ fontSize: "0.88rem", color: "#555", lineHeight: 1.7, margin: 0 }}>{review.comment}</p>
              )}
              {review.userId === user?.id && (
                <div style={{ marginTop: 8, fontSize: "0.72rem", color: secondaryColor, fontWeight: 600 }}>✓ Your review</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
